import { useState, useLayoutEffect, useRef } from "react";
import { Box, IconButton, Dialog, DialogContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Swiper from "swiper";
import { Navigation, Autoplay, EffectCoverflow } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";

import OneBtn from "../../images/btn-gallery/1.jpg";
import TwoBtn from "../../images/btn-gallery/2.jpg";
import ThreeBtn from "../../images/btn-gallery/3.jpg";
import FourBtn from "../../images/btn-gallery/4.jpg";
import FiveBtn from "../../images/btn-gallery/5.jpg";
import SixBtn from "../../images/btn-gallery/6.jpg";
import Eight from "../../images/btn-gallery/8.jpg";
import Nine from "../../images/btn-gallery/9.jpg";

const images = [
    { url: TwoBtn, title: "Питомец 1" },
    { url: OneBtn, title: "Питомец 2" },
    { url: ThreeBtn, title: "Питомец 3" },
    { url: FourBtn, title: "Питомец 4" },
    { url: FiveBtn, title: "Питомец 5" },
    { url: SixBtn, title: "Питомец 6" },
    { url: Eight, title: "Питомец 7" },
    { url: Nine, title: "Питомец 8" },
];

const PetCarousel = () => {
    const [open, setOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    const swiperRef = useRef<HTMLDivElement>(null);

    const handleOpen = (url: string) => {
        setSelectedImage(url);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedImage("");
    };

    useLayoutEffect(() => {
        if (!swiperRef.current) return;
        const timer = setTimeout(() => {
            const swiper = new Swiper(swiperRef.current, {
                modules: [Navigation, Autoplay, EffectCoverflow],
                effect: "coverflow",
                grabCursor: true,
                centeredSlides: true,
                slidesPerView: "auto",
                coverflowEffect: {
                    rotate: 0,
                    stretch: 0,
                    depth: 100,
                    modifier: 2.5,
                    slideShadows: true,
                },
                autoplay: { delay: 4000, disableOnInteraction: false },
                loop: true,
                speed: 800,
            });
            (swiperRef.current as unknown as { swiper?: Swiper }).swiper = swiper;
            return () => {
                if (!swiper.destroyed) swiper.destroy();
            };
        }, 200);
        return () => clearTimeout(timer);
    }, []);

    const goToPrev = () => {
        const swiper = (swiperRef.current as unknown as { swiper?: Swiper })?.swiper;
        if (swiper) swiper.slidePrev();
    };

    const goToNext = () => {
        const swiper = (swiperRef.current as unknown as { swiper?: Swiper })?.swiper;
        if (swiper) swiper.slideNext();
    };

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1200,
                    mx: "auto",
                    my: 4,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    ref={swiperRef}
                    className="pet-swiper"
                    style={{ width: "100%", maxWidth: "90%", overflow: "visible" }}
                >
                    <div className="swiper-wrapper">
                        {images.map((image, idx) => (
                            <div
                                key={idx}
                                className="swiper-slide"
                                style={{
                                    width: 300,
                                    height: 300,
                                    cursor: "pointer",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    padding: 0,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={image.url}
                                    //alt={image.title}
                                    sx={{
                                        width: 300,
                                        height: 300,
                                        objectFit: "cover",
                                        borderRadius: 2,
                                        boxShadow: 2,
                                        transition: "transform 0.3s ease",
                                        "&:hover": { transform: "scale(1.02)" },
                                    }}
                                    onClick={() => handleOpen(image.url)}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        justifyContent: "space-between",
                        px: 2,
                        zIndex: 10,
                    }}
                >
                    <IconButton
                        onClick={goToPrev}
                        aria-label="предыдущее"
                        sx={{
                            bgcolor: "background.paper",
                            color: "secondary.main",
                            boxShadow: 2,
                            "&:hover": {
                                bgcolor: "background.paper",
                                color: "secondary.dark",
                                transform: "scale(1.08)",
                            },
                            borderRadius: "50%",
                            width: 56,
                            height: 56,
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <ArrowBackIcon fontSize="medium" />
                    </IconButton>
                    <IconButton
                        onClick={goToNext}
                        aria-label="следующее"
                        sx={{
                            bgcolor: "background.paper",
                            color: "secondary.main",
                            boxShadow: 2,
                            "&:hover": {
                                bgcolor: "background.paper",
                                color: "secondary.dark",
                                transform: "scale(1.08)",
                            },
                            borderRadius: "50%",
                            width: 56,
                            height: 56,
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <ArrowForwardIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </Box>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", position: "relative" }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            right: 1,
                            top: 1,
                            color: "common.white",
                            bgcolor: "rgba(0,0,0,0.5)",
                            zIndex: 1,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={selectedImage}
                        alt="Full size"
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "90vh",
                            objectFit: "contain",
                            borderRadius: 1,
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PetCarousel;
